import { z } from 'zod';
import { trimmed } from './_fields.js';
import { dateOnlySchema } from './patients.js';
import {
  integrationAppointmentParamSchema,
  integrationAssignSchema,
  integrationJobParamSchema,
  type IntegrationAssignInput,
  type IntegrationAssignResultDto,
  type IntegrationErrorJobDto,
  type IntegrationErrorListDto,
  type IntegrationStreamStatusDto,
  type IntegrationUnmatchedAppointmentDto,
  type IntegrationUnmatchedListDto,
} from './integrations-sync.js';

/**
 * Doctena — la plateforme de prise de rendez-vous en ligne dominante au Luxembourg.
 *
 * Ce fichier porte tout ce que le front et le serveur partagent de l'intégration : réglages,
 * correspondances de calendriers, boîtes de réception, santé des flux. Il est réexporté par
 * `dto/integrations.ts`, qui reste le point d'entrée unique du domaine « intégrations ».
 *
 * **Aucun identifiant n'apparaît ici.** L'email du compte Doctena et le jeton d'API sont des
 * identifiants au sens de `integrationCredentialsSchema` : chiffrés, jamais renvoyés. La V1
 * stockait les deux en clair dans `doctena_config` et les affichait masqués côté client seulement
 * (`docs/audit/audit-schema.md` §7).
 */

/* -------------------------------------------------------------------------- */
/* Réglages                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Les quatre environnements publiés par Doctena. **Liste fermée, et c'est le point** : la V1
 * laissait le cabinet choisir son environnement, donc l'hôte que le serveur allait appeler
 * (`docs/audit/part-routes-D.md`, « API base URLs »). Ici le réglage nomme un environnement, et
 * la passerelle seule connaît l'hôte correspondant — aucune URL libre n'entre jamais en base.
 */
export const doctenaEnvironments = ['testing', 'staging', 'demo', 'production'] as const;
export type DoctenaEnvironment = (typeof doctenaEnvironments)[number];

/**
 * Cadence unique de la synchronisation Doctena, en minutes.
 *
 * Le front l'affiche, la tâche planifiée s'en sert, et le corps d'enregistrement ne peut pas
 * s'en écarter. Une ligne déjà en base peut encore porter une autre valeur : on ne la réécrit
 * pas, on l'ignore.
 */
export const DOCTENA_SYNC_INTERVAL_MINUTES = 5;

/**
 * Bornes encore acceptées **à la lecture** d'une ligne déjà enregistrée. La V1 laissait choisir
 * entre deux et trente minutes ; ces lignes doivent rester lisibles. Ce n'est pas la cadence.
 */
const STORED_SYNC_INTERVAL_MIN_MINUTES = 2;
const STORED_SYNC_INTERVAL_MAX_MINUTES = 30;

export const doctenaSettingsSchema = z
  .object({
    environment: z.enum(doctenaEnvironments),
    /**
     * Le cabinet Doctena (« practice ») que ce compte synchronise, **choisi parmi ceux que le
     * compte voit** — jamais saisi. Un compte Doctena peut couvrir plusieurs adresses, et c'est
     * Doctena qui les révèle une fois l'email et la clé éprouvés : la V1 déduisait la liste des
     * cabinets des calendriers rendus et la proposait dans un sélecteur. Nul tant que le choix
     * n'est pas fait ; rien ne se synchronise avant, et l'écran le dit.
     */
    practiceId: trimmed(32).nullable(),
    /** Son nom au moment du choix, pour que l'écran le nomme sans rappeler Doctena. */
    practiceName: trimmed(200).nullable().default(null),
    /**
     * Date civile (`yyyy-MM-dd`) à partir de laquelle l'agenda Doctena est importé. Nulle, la
     * fenêtre glissante par défaut du tirage complet s'applique. Elle est posée par « Recharger
     * tout l'agenda depuis cette date », le geste de la V1 : un cabinet qui rejoint OstéoPro en
     * cours d'année ne veut pas de son historique Doctena entier.
     */
    syncStartDate: dateOnlySchema.nullable().default(null),
    /**
     * Valeur historique, ignorée. La cadence réelle est `DOCTENA_SYNC_INTERVAL_MINUTES` : la
     * tâche ne lit pas ce champ, et l'écran ne le propose plus.
     */
    syncIntervalMinutes: z
      .int()
      .min(STORED_SYNC_INTERVAL_MIN_MINUTES)
      .max(STORED_SYNC_INTERVAL_MAX_MINUTES),
    /** Faux = lecture seule : Doctena fait autorité et rien ne repart. C'est le défaut du produit. */
    bidirectionalSync: z.boolean(),
    /**
     * Conservé pour que les réglages déjà enregistrés restent lisibles. Ignoré : un rendez-vous
     * sans dossier reste sur l'agenda, et le dossier se crée au démarrage de la consultation.
     */
    autoCreatePatients: z.boolean(),
  })
  .strict();
export type DoctenaSettings = z.infer<typeof doctenaSettingsSchema>;

/**
 * Corps d'enregistrement et d'essai. Toute cadence autre que la constante est refusée. La
 * lecture des lignes déjà enregistrées reste `doctenaSettingsSchema`, qui accepte encore
 * l'ancienne plage pour ne pas rendre une connexion illisible.
 */
export const doctenaSettingsInputSchema = doctenaSettingsSchema
  .extend({
    syncIntervalMinutes: z.literal(DOCTENA_SYNC_INTERVAL_MINUTES),
  })
  .strict();

/* -------------------------------------------------------------------------- */
/* Flux                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Les trois flux suivis séparément dans `integration_health` : deux tirages par curseur et la
 * poussée. La V1 alignait trente colonnes de compteurs sur `doctena_config` pour dire la même
 * chose ; une ligne par flux rend l'ajout d'un flux gratuit.
 */
export const doctenaStreams = ['appointments', 'patients', 'push'] as const;
export type DoctenaStream = (typeof doctenaStreams)[number];

/* -------------------------------------------------------------------------- */
/* Correspondances de calendriers                                              */
/* -------------------------------------------------------------------------- */

/** Un calendrier distant, tel que Doctena le décrit. Lecture seule, sert à l'écran d'association. */
export interface DoctenaCalendarDto {
  calendarId: string;
  calendarName: string;
  practiceId: string;
  practiceName: string;
  /** Membre du cabinet déjà associé à ce calendrier, ou nul. */
  practitionerMemberId: string | null;
}

/**
 * Un cabinet Doctena, tel que le compte le voit. Un praticien peut exercer à plusieurs adresses ;
 * la connexion n'en synchronise qu'une, choisie dans cette liste — jamais saisie à la main.
 */
export interface DoctenaPracticeDto {
  practiceId: string;
  practiceName: string;
}

/**
 * Les cabinets distincts d'une liste de calendriers, dans l'ordre de première apparition. C'est
 * la déduction que faisait l'écran de la V1 ; elle vit ici pour que le serveur et l'écran
 * n'en aient qu'une.
 */
export function doctenaPracticesOf(
  calendars: readonly Pick<DoctenaCalendarDto, 'practiceId' | 'practiceName'>[],
): DoctenaPracticeDto[] {
  const seen = new Map<string, DoctenaPracticeDto>();
  for (const calendar of calendars) {
    if (!seen.has(calendar.practiceId)) {
      seen.set(calendar.practiceId, {
        practiceId: calendar.practiceId,
        practiceName: calendar.practiceName,
      });
    }
  }
  return [...seen.values()];
}

export interface DoctenaCalendarListDto {
  /**
   * Les calendriers du cabinet choisi. **Vide tant qu'aucun cabinet n'est choisi** : Doctena
   * liste les calendriers par cabinet, et l'écran propose d'abord le choix du cabinet.
   */
  calendars: DoctenaCalendarDto[];
  /** Les cabinets que le compte voit, pour choisir — ou changer — celui qu'on synchronise. */
  practices: DoctenaPracticeDto[];
}

/**
 * Enregistrement en bloc des associations. La liste reçue **remplace** l'existante : c'est la
 * sémantique de l'écran (une carte par praticien, un sélecteur, un bouton « Enregistrer »), et
 * elle évite le lot de routes unitaires de la V1. Un calendrier ne peut aller qu'à un praticien,
 * et un praticien ne peut recevoir qu'un calendrier : le service refuse les doublons.
 */
export const doctenaCalendarLinksSchema = z
  .object({
    links: z
      .array(z.object({ calendarId: trimmed(64), practitionerMemberId: z.uuid() }).strict())
      .max(100),
  })
  .strict();
export type DoctenaCalendarLinksInput = z.infer<typeof doctenaCalendarLinksSchema>;

/* -------------------------------------------------------------------------- */
/* Boîtes de réception                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Rien de propre à Doctena ici : rendez-vous non rattachés, rattachement manuel, incidents et
 * santé des flux décrivent nos tables, pas son protocole. Les contrats vivent donc dans
 * `dto/integrations-sync.ts`, partagés avec Docalia, et ces alias gardent les noms que le front
 * emploie déjà.
 */
export type DoctenaUnmatchedAppointmentDto = IntegrationUnmatchedAppointmentDto;
export type DoctenaUnmatchedListDto = IntegrationUnmatchedListDto;
export const doctenaAssignSchema = integrationAssignSchema;
export type DoctenaAssignInput = IntegrationAssignInput;
export type DoctenaAssignResultDto = IntegrationAssignResultDto;
export type DoctenaErrorJobDto = IntegrationErrorJobDto;
export type DoctenaErrorListDto = IntegrationErrorListDto;

/* -------------------------------------------------------------------------- */
/* État                                                                        */
/* -------------------------------------------------------------------------- */

/** Santé d'un flux Doctena. La structure est commune ; seule la liste des flux est à lui. */
export type DoctenaStreamStatusDto = IntegrationStreamStatusDto<DoctenaStream>;

export interface DoctenaStatusDto {
  isEnabled: boolean;
  hasCredentials: boolean;
  settings: DoctenaSettings | null;
  /** Nombre de calendriers associés à un praticien. Zéro = rien ne sera jamais tiré. */
  linkedCalendars: number;
  /** Rendez-vous en attente de rattachement, et travaux en échec. */
  unmatchedCount: number;
  failedJobCount: number;
  /** Vrai tant qu'aucun tirage complet n'a réussi : la prochaine synchronisation sera FULL. */
  needsFullSync: boolean;
  /** Liens de rendez-vous mis en pause par la garde anti-boucle de poussée. */
  pushPausedCount: number;
  /**
   * Rendez-vous importés, à venir, qui en chevauchent un autre : importés quand même et signalés
   * « conflit à régler » dans l'agenda (décision du 30 septembre 2026).
   */
  conflictCount: number;
  streams: DoctenaStreamStatusDto[];
}

/* -------------------------------------------------------------------------- */
/* Déclenchement                                                               */
/* -------------------------------------------------------------------------- */

/** Ce qu'une synchronisation déclarée terminée a réellement fait. Aucun compte de patients nommés. */
export interface DoctenaSyncResultDto {
  mode: 'full' | 'watch';
  created: number;
  updated: number;
  cancelled: number;
  unmatched: number;
  pushed: number;
  errors: number;
}

/**
 * « Recharger tout l'agenda depuis cette date », le geste de la V1 (`button-reload-full-agenda`).
 *
 * Le serveur **efface** les rendez-vous d'origine Doctena à partir de la date qui ne sont pas
 * protégés — ni consultation (donc ni facture ni paiement), ni issue notée (réalisé, manqué), ni
 * notes privées —, oublie le curseur du flux des rendez-vous, retient la date dans les réglages,
 * puis synchronise : le tirage repart complet depuis cette date. Les protégés sont gardés et
 * simplement reliés au passage. Les rendez-vous saisis au cabinet ne sont jamais touchés.
 */
export const doctenaImportSchema = z.object({ startDate: dateOnlySchema }).strict();
export type DoctenaImportInput = z.infer<typeof doctenaImportSchema>;

/** Aperçu sans écriture, lu par le dialogue de confirmation avant le geste. */
export interface DoctenaImportPreviewDto {
  startDate: string;
  /** Rendez-vous Doctena qui seraient effacés puis réimportés. */
  deletable: number;
  /** Rendez-vous Doctena gardés parce qu'ils sont protégés. */
  kept: number;
}

export interface DoctenaImportResultDto {
  startDate: string;
  /** Rendez-vous Doctena effacés avant la réimportation. */
  deleted: number;
  /** Gardés parce qu'ils sont protégés (consultation, issue notée, notes privées). */
  kept: number;
  /** Le passage de synchronisation lancé dans la foulée. */
  sync: DoctenaSyncResultDto;
}

/** Mêmes paramètres de route que Docalia : la boîte de réception est la même. */
export const doctenaAppointmentParamSchema = integrationAppointmentParamSchema;
export const doctenaJobParamSchema = integrationJobParamSchema;
